import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusChip } from "@/components/StatusChip";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Trash2, MessageSquare, Phone, Smartphone, QrCode } from "lucide-react";

export default function WhatsAppPage() {
  const [conn, setConn] = useState({ status: "disconnected" });
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/connection");
      setConn(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const startConnect = async () => {
    setBusy(true);
    try {
      await api.post("/whatsapp/connect");
      toast.info("جاري تجهيز رمز QR، انتظر لحظات...");
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/whatsapp/connection");
      toast.success("تم إلغاء الربط");
      setConn({ status: "disconnected" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">ربط واتساب المدرسة</h1>
          <p className="text-slate-400">اربط رقم واتساب مدرستك عبر رمز QR لبدء إرسال الرسائل</p>
        </div>
        <StatusChip status={conn.status} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4 text-emerald-700" /> حالة الاتصال
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {conn.connected ? (
              <div className="space-y-4">
                <div className="rounded-xl bg-green-50 p-4">
                  <p className="mb-2 font-bold text-green-800">🟢 متصل — القناة جاهزة للإرسال</p>
                  <div className="flex items-center gap-2 text-sm text-green-900">
                    <Phone className="h-4 w-4 shrink-0" />
                    <span>رقم واتساب المدرسة: <b dir="ltr" className="font-mono">{conn.phone ? `+${conn.phone}` : "—"}</b></span>
                  </div>
                </div>
                <Button variant="outline" onClick={disconnect} className="gap-2 text-red-600" data-testid="disconnect-button">
                  <Trash2 className="h-4 w-4" /> إلغاء الربط
                </Button>
              </div>
            ) : conn.qr ? (
              <div className="flex flex-col items-center gap-4 py-2 text-center">
                <p className="font-semibold text-slate-700">امسح رمز QR من تطبيق واتساب على جوال المدرسة</p>
                <img src={conn.qr} alt="QR" className="h-64 w-64 rounded-2xl border-4 border-emerald-100 bg-white p-2" data-testid="whatsapp-qr" />
                <div className="rounded-xl bg-slate-50 p-3 text-right text-sm text-slate-500">
                  <p>افتح واتساب على الجوال ← <b>الإعدادات</b> ← <b>الأجهزة المرتبطة</b> ← <b>ربط جهاز</b> ← وجّه الكاميرا للرمز.</p>
                </div>
                <p className="flex items-center gap-2 text-xs text-amber-600">
                  <Loader2 className="h-3 w-3 animate-spin" /> بانتظار المسح... يتم تحديث الحالة تلقائياً
                </p>
              </div>
            ) : conn.status === "connecting" ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center text-slate-600">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
                <p className="font-semibold">جاري إعادة الاتصال بجلسة واتساب السابقة...</p>
                <p className="text-xs text-slate-400">إذا استمر الأمر طويلاً، اضغط إلغاء الربط ثم أعد المسح.</p>
                <Button variant="outline" onClick={disconnect} className="gap-2 text-red-600" data-testid="reset-connection-button">
                  <Trash2 className="h-4 w-4" /> إلغاء الربط وإعادة المسح
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">
                  🔴 غير متصل — لم يتم ربط رقم واتساب لهذه المدرسة بعد
                </div>
                <Button onClick={startConnect} disabled={busy}
                  className="h-12 gap-2 bg-emerald-800 text-base font-bold hover:bg-emerald-900"
                  data-testid="connect-whatsapp-button">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <QrCode className="h-5 w-5" />}
                  ربط واتساب المدرسة
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-emerald-700" /> إرسال بشري وآمن
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <p className="flex gap-2"><Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> يبقى جوال المدرسة متصلاً بالإنترنت ليعمل الإرسال.</p>
            <p className="flex gap-2"><MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> تُرسل الرسائل واحدة تلو الأخرى بفاصل زمني (نمط بشري) — لا يتم إرسال دفعة واحدة.</p>
            <p className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> رسالة منفصلة لكل ولي أمر، وليست مجموعة.</p>
            <p className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> اتصال كل مدرسة مستقل تماماً عن باقي المدارس.</p>
            <p className="text-xs text-slate-400">تنبيه: الإرسال الجماعي المتكرر قد يعرّض الرقم لقيود من واتساب. استخدم الميزة باعتدال.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
